import { useCallback, useRef, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import copy from 'clipboard-copy';

import { gql } from '@graphql/__generated__';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { useLocale } from '@shared/hooks/locale';

import { copyPromiseToClipboard } from './clipboard';
import { waitForPrintResources } from './print-resources';

const TRACKED_DOCUMENT_URL = gql(/* GraphQL */ `
  mutation TrackedDocumentUrl(
    $url: String!
    $documentType: String
    $channel: String
  ) {
    tracked_document_url(
      url: $url
      document_type: $documentType
      channel: $channel
    ) {
      url
    }
  }
`);

/**
 * Mints a trackable share link for a docs document URL (signed + shortened
 * server-side so opens land in BigQuery `docs_tracking.opens`).
 *
 * Call `copyLink(onCopied)` to mint and copy the link. When the link is already
 * resolved the copy is synchronous; otherwise it is minted on the fly using a
 * `ClipboardItem` promise to preserve the user gesture (with an await +
 * `writeText` fallback). Falls back to the plain docs URL if minting fails.
 */
export const useTrackedDocumentLink = (
  documentUrl: URL,
  documentType: string,
  channel = 'manual',
) => {
  const [trackedUrl, setTrackedUrl] = useState<string | null>(null);
  const [isMinting, setIsMinting] = useState(false);
  const [copiedWithoutTracking, setCopiedWithoutTracking] = useState(false);
  const [getTrackedUrl] = useMutation(TRACKED_DOCUMENT_URL);
  const trackedForRef = useRef<string | null>(null);
  const inFlightRef = useRef<Promise<string> | null>(null);

  const fetchTrackedUrl = useCallback((): Promise<string> => {
    const href = documentUrl.href;
    if (trackedForRef.current === href && inFlightRef.current != null) {
      return inFlightRef.current;
    }
    trackedForRef.current = href;
    setTrackedUrl(null);
    setIsMinting(true);
    const promise = getTrackedUrl({
      variables: { url: href, documentType, channel },
    })
      .then(res => {
        const result = res.data?.tracked_document_url?.url;
        if (result == null) {
          throw new Error('Failed to mint tracked document link');
        }
        if (trackedForRef.current === href) {
          setTrackedUrl(result);
        }
        return result;
      })
      .catch(error => {
        console.warn('Failed to mint tracked document link:', error);
        // Reset only the href marker so a retry re-mints. Keep the promise
        // reference until .finally so this request can clear its loading state.
        if (inFlightRef.current === promise) {
          trackedForRef.current = null;
        }
        return href;
      })
      .finally(() => {
        if (inFlightRef.current === promise) {
          setIsMinting(false);
        }
      });
    inFlightRef.current = promise;
    return promise;
  }, [documentUrl, documentType, channel, getTrackedUrl]);

  const resetCopyWarning = useCallback(() => {
    setCopiedWithoutTracking(false);
  }, []);

  const copyLink = useCallback(
    (onCopied?: () => void) => {
      setCopiedWithoutTracking(false);
      const href = documentUrl.href;
      const handleCopied = (url: string) => {
        if (url === href) {
          setCopiedWithoutTracking(true);
        } else {
          onCopied?.();
        }
      };
      // Already resolved for this URL: copy synchronously (most reliable).
      if (trackedForRef.current === documentUrl.href && trackedUrl != null) {
        copy(trackedUrl)
          .then(() => handleCopied(trackedUrl))
          .catch(error => console.error('Failed to copy link:', error));
        return;
      }

      const urlPromise = fetchTrackedUrl();
      copyPromiseToClipboard(urlPromise, {
        onCopied: () => void urlPromise.then(handleCopied),
        onError: error => console.error('Failed to copy link:', error),
      });
    },
    [documentUrl, trackedUrl, fetchTrackedUrl],
  );

  return {
    copyLink,
    trackedUrl,
    isMinting,
    copiedWithoutTracking,
    resetCopyWarning,
  };
};

export const useReportPrintHandler = (
  iFrameRef: React.MutableRefObject<HTMLIFrameElement | null>,
) => {
  const { setError } = useReportContext();
  const { t } = useLocale();
  return useCallback(async () => {
    const contentWindow = iFrameRef.current?.contentWindow;
    if (!contentWindow) {
      return;
    }

    const content = contentWindow.document.getElementById('content');
    if (!content) {
      return;
    }

    const style = contentWindow.document.createElement('style');
    style.textContent = `
      #content {
        position: relative !important;
        inset: 0 !important;
        transform: none !important;
      }

      .pagedjs_pages > .pagedjs_page {
        margin-bottom: 0mm;
        margin-top: 0mm;
      }
    `;

    try {
      await waitForPrintResources(contentWindow.document);
      // The preview may have been replaced while its pictures were loading.
      if (iFrameRef.current?.contentWindow !== contentWindow) {
        return;
      }
      contentWindow.document.head.appendChild(style);
      contentWindow.print();
    } catch (error) {
      console.error('Could not print report:', error);
      setError(new Error(t('The preview could not be loaded.')));
    } finally {
      style.remove();
    }
  }, [iFrameRef, setError, t]);
};

export const useReportDownloadHandler = (documentUrl: URL) => {
  return useCallback(
    (translatedFileName: string | undefined) => {
      try {
        if (!translatedFileName) {
          console.error('No filename provided for download');
          return;
        }

        const pdfUrl = new URL(documentUrl.href);
        const encodedFilename = encodeURIComponent(translatedFileName);
        pdfUrl.pathname =
          pdfUrl.hostname === 'localhost'
            ? `/generate-pdf/${encodedFilename}`
            : `/pdf/${encodedFilename}`;
        window.open(pdfUrl.href);
      } catch (error) {
        console.error('Failed to download report:', error);
      }
    },
    [documentUrl],
  );
};
